import { api, prefix, generateQueryParamFromObj } from "@/api";
import { useSettings } from "@/store/settings";
import type { AxiosRequestConfig } from "axios";
import type {
  AssigneeType,
  ContactTypesType,
  LeadActionParamsType,
  LeadActionType,
  LeadsParamsType,
  LeadsReferralSearchItemType,
  LeadsReferralType,
  LeadType,
} from "@/types/lead";

const namespace = "gaia";

// fetch contact types from be based on applicationIds and landingCodes
export const getContactTypes = (
  landingCodes?: string[],
  applicationIds?: string[],
  config: AxiosRequestConfig = {}
) => {
  const ids = applicationIds ?? [String(useSettings().effectiveApplicationId)];
  let query = `&applicationsIds=${ids.join(",")}`;
  if (landingCodes) {
    query += `&landingCodes=${landingCodes.join(",")}`;
  }
  return api<{ data: ContactTypesType[] }>({
    url: `${prefix}/${namespace}/contact-types?${query}`,
    method: "get",
    ...config,
  });
};

export const getListReport = (
  params: LeadsParamsType = {},
  excludeStatuses?: string[], // filtro negativo per escludere alcuni status
  config: AxiosRequestConfig = {}
) => {
  let query = generateQueryParamFromObj(params);
  if (excludeStatuses && excludeStatuses.length) {
    query += `&excludeStatuses=${excludeStatuses.join(",")}`;
  }
  return api<{ data: LeadType[]; count: number }>({
    url: `${prefix}/${namespace}/leads?${query}`,
    method: "get",
    ...config,
  });
};

export const getListReportV2 = (
  params: LeadsParamsType = {},
  excludeStatuses?: string[], // filtro negativo per escludere alcuni status
  config: AxiosRequestConfig = {}
) => {
  let query = generateQueryParamFromObj(params);
  if (excludeStatuses && excludeStatuses.length) {
    query += `&excludeStatuses=${excludeStatuses.join(",")}`;
  }
  return api<{ data: LeadType[]; count: number }>({
    url: `${prefix}/${namespace}/leads/v2?${query}`,
    method: "get",
    ...config,
  });
};

export const getListAssignee = (config: AxiosRequestConfig = {}) => {
  return api<{ data: AssigneeType[] }>({
    url: `${prefix}/${namespace}/leads/assignees`,
    method: "get",
    ...config,
  });
};

export const createLead = (params: object, config: AxiosRequestConfig = {}) => {
  return api<{
    existing: boolean;
    id: number;
    status: string;
  }>({
    url: `${prefix}/${namespace}/leads`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const updateLead = (
  id: number | string,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  return api<{
    invitation: LeadType & { id: number };
    actions: Array<LeadActionType>;
  }>({
    url: `${prefix}/${namespace}/leads/${id}`,
    method: "PUT",
    data: params,
    ...config,
  });
};

export const getLeadInvitation = (id: number, config: AxiosRequestConfig = {}) => {
  return api<{
    invitation: LeadType & { id: number };
    actions: Array<LeadActionType>;
  }>({
    url: `${prefix}/${namespace}/leads/${id}/details`,
    method: "GET",
    ...config,
  });
};

export const assignLead = (
  id: number | string,
  params: {
    organizationId: number;
    roleId: number;
    userId: string;
  },
  config: AxiosRequestConfig = {}
) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/leads/${id}/assignees`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const deleteAssignedLead = (id: number | string, config: AxiosRequestConfig = {}) => {
  return api<void>({
    url: `${prefix}/${namespace}/leads/assignees/${id}`,
    method: "DELETE",
    ...config,
  });
};

export const addAction = (params: LeadActionParamsType, config: AxiosRequestConfig = {}) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/actions`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const addAttachmentToActionById = (
  id: number | string,
  file: File,
  fields?: Record<string, string>,
  config: AxiosRequestConfig = {}
) => {
  const formData = new FormData();
  formData.append("file", file);
  
  if( fields ){
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
  } 

  return api<{ id: number }>({
    url: `${prefix}/${namespace}/actions/${id}/attachment`,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      ...config.headers,
    },
    ...config,
  });
};

export const getActionAttachmentById = (attachmentId: number, config: AxiosRequestConfig = {}) => {
  return api<Blob>({
    url: `${prefix}/gaia/actions/attachment/${attachmentId}`,
    method: "GET",
    responseType: "blob",
    ...config,
  });
};

export const deleteActionAttachmentById = (
  attachmentId: number,
  config: AxiosRequestConfig = {}
) => {
  return api<void>({
    url: `${prefix}/gaia/actions/attachment/${attachmentId}`,
    method: "DELETE",
    ...config,
  });
};

export const updateAction = (
  id: number | string,
  params: LeadActionParamsType,
  config: AxiosRequestConfig = {}
) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/actions/${id}`,
    method: "PUT",
    data: params,
    ...config,
  });
};

export const getInvitationInfoFromPhoneNumber = (
  phoneNumber: string,
  config: any = { suppressErrorHandling: true }
) => {
  return api<any>({
    url: `${prefix}/${namespace}/leads/phone/${phoneNumber}`,
    method: "GET",
    ...config,
  });
};

export const inviteLead = (
  invitationId: number,
  params: {
    networkId: number | null;
    applicationId: number;
  },
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/leads/${invitationId}`,
    method: "PATCH",
    data: params,
    ...config,
  });
};

export const getLeadStatus = (config: AxiosRequestConfig = {}) => {
  return api<object>({
    url: `${prefix}/${namespace}/leads/statuses`,
    method: "GET",
    ...config,
  });
};

export const getLeadStatusChanges = (config: AxiosRequestConfig = {}) => {
  return api<object>({
    url: `${prefix}/${namespace}/leads/statuschanges`,
    method: "GET",
    ...config,
  });
};

export const getLeadReferral = (config: AxiosRequestConfig = {}) => {
  return api<{ data: LeadsReferralType[] }>({
    url: `${prefix}/${namespace}/leads/referrals`,
    method: "GET",
    ...config,
  });
};

/**
 * Cerca le organizzazioni con codice referral per ragione sociale o partita IVA.
 * Il BE risponde 400 sotto i 3 caratteri, quindi la soglia si rispetta qui.
 */
export const searchLeadReferrals = (
  searchText: string,
  limit = 20,
  config: AxiosRequestConfig = {}
) => {
  if (searchText.trim().length < 3) {
    throw new Error("searchLeadReferrals: searchText requires at least 3 characters");
  }

  // generateQueryParamFromObj non fa encoding e la ragione sociale può contenere spazi e &
  const query = generateQueryParamFromObj({
    searchText: encodeURIComponent(searchText.trim()),
    limit,
  });

  // api<T> è dichiarato AxiosResponse<T> ma l'interceptor risolve già il body:
  // il tipo del payload è quindi quello di `response.data`
  return api<LeadsReferralSearchItemType[]>({
    url: `${prefix}/${namespace}/leads/referrals/search?${query}`,
    method: "GET",
    ...config,
  });
};

/**
 * Corregge il referral di un lead esistente (backoffice).
 * Rotta dedicata: `PUT /leads/{id}` non ha il controllo di permesso né la validazione del codice.
 */
export const updateLeadReferral = (
  invitationId: number,
  params: { referral: string },
  config: AxiosRequestConfig = {}
) => {
  return api<void>({
    url: `${prefix}/${namespace}/leads/${invitationId}/referral`,
    method: "PUT",
    data: params,
    ...config,
  });
};

export const createInvitationEntity = (
  leadId: number,
  params: { entityId?: number; entityType?: string } | null,
  config: AxiosRequestConfig = {}
) => {
  return api<{ id: number }>({
    url: `${prefix}/${namespace}/leads/${leadId}/entity`,
    method: "POST",
    data: params,
    ...config,
  });
};

export const getOrigins = (config: AxiosRequestConfig = {}) => {
  return api<{ data: { origin: string }[]; count: number }>({
    url: `${prefix}/${namespace}/origins`,
    method: "GET",
    ...config,
  });
};
